import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCatalogSpecialNotice } from "@/hooks/use-catalog-special-notices";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import {
  Badge,
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  PageHeader,
  Skeleton,
  catalogStatusVariant,
} from "@/components/ui";

export const Route = createFileRoute("/_authenticated/catalog/special-notices/$id")({
  component: CatalogSpecialNoticeDetailPage,
});

function CatalogSpecialNoticeDetailPage() {
  const { t } = useTranslation(["catalog", "common", "nav"]);
  const { id } = useParams({ from: "/_authenticated/catalog/special-notices/$id" });

  const { data: notice, isLoading, isError } = useCatalogSpecialNotice(id);
  useRecentPageTitle(notice?.title ?? undefined);

  if (isLoading) {
    return (
      <div className="space-y-4 p-8">
        <Skeleton variant="line" />
        <Skeleton variant="card" />
      </div>
    );
  }

  if (isError || !notice) {
    return (
      <div className="p-8">
        <Breadcrumbs
          items={[
            { label: t("catalog.group", { ns: "nav" }) },
            { label: t("page.specialNoticesTitle"), to: "/catalog/special-notices" },
            { label: t("page.specialNoticeDetailTitle") },
          ]}
        />
        <PageHeader title={t("page.specialNoticeDetailTitle")} />
        <div className="mt-6">
          <EmptyState
            title={t("page.specialNoticeDetailTitle")}
            description={t("errors.loadFailed")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("catalog.group", { ns: "nav" }) },
          { label: t("page.specialNoticesTitle"), to: "/catalog/special-notices" },
          { label: notice.notice_id },
        ]}
      />
      <PageHeader
        title={notice.title}
        subtitle={`${notice.notice_id} — ${String(notice.notice_year)}`}
        actions={
          <Badge variant={catalogStatusVariant(notice.status)}>
            {t(`status.${notice.status}`)}
          </Badge>
        }
      />

      {notice.superseded_by_notice_id && (
        <div className="mt-6 rounded-md bg-amber-50 p-4 dark:bg-amber-900/20">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {t("specialNotice.supersededBanner", {
              noticeId: notice.superseded_by_notice_id,
            })}{" "}
            <Link
              to="/catalog/special-notices/$id"
              params={{ id: notice.superseded_by_notice_id }}
              className="font-medium underline"
            >
              {t("assembly.links.viewSpecialNotice")}
            </Link>
          </p>
        </div>
      )}

      <Card className="mt-6">
        <CardHeader>
          <h3 className="text-base font-semibold text-on-surface">
            {t("specialNotice.fields.body")}
          </h3>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-line text-sm text-on-surface">{notice.body}</p>
        </CardContent>
      </Card>

      {notice.affected_manufacturers_hint.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <h3 className="text-base font-semibold text-on-surface">
              {t("import.entityType.manufacturer")}
            </h3>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {notice.affected_manufacturers_hint.map((name) => (
                <Badge key={name} variant="default">
                  {name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
